import { useCallback, useEffect, useState } from "react";
import { qrAgenzieApi, type QrAgenziaRiga } from "@/api/qrAgenzie";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

// Stesso pattern toggle di Listino.tsx (button role="switch" + pallino animato):
// riusato qui per il master switch e per l'interruttore di ogni riga.
function Interruttore({
  acceso, onClick, title, ariaLabel,
}: {
  acceso: boolean;
  onClick: () => void;
  title?: string;
  ariaLabel?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={acceso}
      aria-label={ariaLabel}
      onClick={onClick}
      title={title}
      className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/50 ${
        acceso ? "bg-primary" : "bg-input"
      }`}
    >
      <span
        className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-lg transition-transform ${
          acceso ? "translate-x-5" : "translate-x-0"
        }`}
      />
    </button>
  );
}

type Toast = { msg: string; type: "ok" | "err" };

/**
 * Console amministratore «QR Self Service Clienti» (spec §3.3): interruttore
 * generale in testa e, per ciascuna agenzia, lo stesso interruttore «QR per i
 * clienti» e il pulsante «Nuovo codice» (con conferma) per rigenerare il codice.
 * Ogni azione è ottimistica: se la chiamata fallisce lo stato torna quello di
 * prima e compare l'avviso (stesso pattern/testo del toast di Listino.tsx).
 */
export function QrAgenzie() {
  const [interruttore, setInterruttoreState] = useState(false);
  const [agenzie, setAgenzie] = useState<QrAgenziaRiga[]>([]);
  const [caricamento, setCaricamento] = useState(true);
  const [rifaiTarget, setRifaiTarget] = useState<QrAgenziaRiga | null>(null);
  const [inCorso, setInCorso] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);

  function showToast(msg: string, type: Toast["type"]) {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  }

  const carica = useCallback(() => {
    setCaricamento(true);
    qrAgenzieApi
      .lista()
      .then((r) => {
        setInterruttoreState(r.interruttore);
        setAgenzie(r.agenzie);
      })
      .finally(() => setCaricamento(false));
  }, []);

  useEffect(() => {
    carica();
  }, [carica]);

  async function toggleGenerale() {
    const precedente = interruttore;
    const nuovo = !precedente;
    setInterruttoreState(nuovo);
    try {
      await qrAgenzieApi.interruttore(nuovo);
    } catch {
      setInterruttoreState(precedente);
      showToast("Errore aggiornamento stato", "err");
    }
  }

  async function toggleRiga(r: QrAgenziaRiga) {
    const precedente = r;
    setAgenzie((prev) =>
      prev.map((x) => (x.espo_account_id === r.espo_account_id ? { ...x, attivo: !r.attivo } : x)));
    try {
      const risposta = r.attivo
        ? await qrAgenzieApi.disattiva(r.espo_account_id)
        : await qrAgenzieApi.attiva(r.espo_account_id);
      setAgenzie((prev) =>
        prev.map((x) => (x.espo_account_id === r.espo_account_id ? { ...x, ...risposta } : x)));
    } catch {
      setAgenzie((prev) =>
        prev.map((x) => (x.espo_account_id === r.espo_account_id ? precedente : x)));
      showToast("Errore aggiornamento stato", "err");
    }
  }

  async function confermaRifai() {
    if (!rifaiTarget) return;
    const target = rifaiTarget;
    setInCorso(true);
    try {
      const risposta = await qrAgenzieApi.rifai(target.espo_account_id);
      setAgenzie((prev) =>
        prev.map((x) => (x.espo_account_id === target.espo_account_id ? { ...x, ...risposta } : x)));
      setRifaiTarget(null);
    } catch {
      setRifaiTarget(null);
      showToast("Errore aggiornamento stato", "err");
    } finally {
      setInCorso(false);
    }
  }

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-lg font-semibold">QR Self Service Clienti</h2>
      </div>

      <div className="bg-card p-4 rounded-lg border border-border flex items-center justify-between gap-4">
        <div>
          <p className="font-semibold text-foreground">Interruttore generale</p>
          <p className="text-sm text-muted-foreground">
            Se è spento, nessun QR funziona, anche per le agenzie attive.
          </p>
        </div>
        <Interruttore
          acceso={interruttore}
          onClick={() => void toggleGenerale()}
          title={interruttore ? "Disattiva" : "Attiva"}
        />
      </div>

      {caricamento ? (
        <p className="text-sm text-muted-foreground">Caricamento...</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-border bg-card">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Agenzia</TableHead>
                <TableHead className="whitespace-normal align-top py-2">
                  <span className="block font-medium text-foreground">QR per i clienti</span>
                  <span className="block text-xs font-normal text-muted-foreground">
                    Il cliente fa la pratica da solo, dal suo telefono
                  </span>
                </TableHead>
                <TableHead>Link</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {agenzie.map((a) => (
                <TableRow key={a.espo_account_id}>
                  <TableCell className="text-foreground font-medium align-top">{a.nome}</TableCell>
                  <TableCell className="align-top">
                    <div className="flex items-center gap-3">
                      <Interruttore
                        acceso={a.attivo}
                        onClick={() => void toggleRiga(a)}
                        title={a.attivo ? "Disattiva" : "Attiva"}
                        ariaLabel={`QR per i clienti – ${a.nome ?? a.espo_account_id}`}
                      />
                      {a.attivo && (
                        <Badge variant="outline" className="border-emerald-400 text-emerald-700">
                          Attivo
                        </Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-xs align-top">
                    {a.link ? <code className="break-all">{a.link}</code> : <span>—</span>}
                  </TableCell>
                  <TableCell className="text-right align-top">
                    {a.attivo && (
                      <Button size="sm" variant="outline" onClick={() => setRifaiTarget(a)}>
                        Nuovo codice
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {rifaiTarget && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => !inCorso && setRifaiTarget(null)}
        >
          <div
            className="bg-card text-foreground rounded-xl max-w-md w-full p-6 shadow-xl border border-border"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-base font-semibold mb-2">Nuovo codice</h3>
            <p className="text-sm text-muted-foreground mb-5">
              Il codice attuale smette di funzionare e il cartello va ristampato. Continuare?
            </p>
            <div className="flex justify-end gap-2">
              <button
                type="button"
                disabled={inCorso}
                onClick={() => setRifaiTarget(null)}
                className="px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted transition-colors disabled:opacity-50"
              >
                Annulla
              </button>
              <button
                type="button"
                disabled={inCorso}
                onClick={() => void confermaRifai()}
                className="px-3 py-1.5 text-sm bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50"
              >
                Nuovo codice
              </button>
            </div>
          </div>
        </div>
      )}

      {toast && (
        <div
          className={`fixed bottom-6 right-6 z-50 px-4 py-3 rounded-lg text-sm shadow-lg border ${
            toast.type === "ok"
              ? "bg-green-100 text-green-800 border-green-200"
              : "bg-red-100 text-red-800 border-red-200"
          }`}
        >
          {toast.msg}
        </div>
      )}
    </div>
  );
}
